<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="330"
    @click:outside="onCancelButton"
    @keydown.esc="onCancelButton"
  >
    <v-card class="popup-wrapper move-ip-popup">
      <div class="title">{{$t(title)}}</div>
      <div class='select-host'>
        <span class="select-host__title">{{ $t("configuration.add.url.associate.ip") }}</span>
        <v-autocomplete
          class-name="move-ip-domain-select"
          :placeholder="$t('select')"
          :items='ipList'
          :append-icon="'expand_more'"
          v-model='selectedIp'
          dense
          outlined
          autofocus
          return-object
        />
      </div>
      <div class='select-domain'>
        <span class="select-domain__title">{{ $t("configuration.add.url.associate.tld") }}</span>
        <v-autocomplete
          class="move-ip-domain-select"
          :placeholder="$t('select')"
          :items='tldKeys'
          :append-icon="'expand_more'"
          v-model='selectedTld'
          dense
          outlined
          autofocus
          return-object
          @change='loadSubdomains(selectedTld)'
        />
      </div>
      <div class='select-subdomain'>
        <span class="select-subdomain__title">{{ $t("configuration.add.url.associate.subdomain") }}</span>
        <v-autocomplete
          class-name="move-ip-domain-select"
          :placeholder="$t('select')"
          :items='subdomainsOptions'
          :append-icon="'expand_more'"
          :loading='loadingSubdomains'
          v-model='selectedSubdomain'
          dense
          outlined
          autofocus
          return-object
        />
      </div>
      <div class="buttons">
        <v-btn small depressed class="no-button btn-secondary" @click="onCancelButton()">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn small depressed class="yes-button btn-primary" @click="onOkClickButton()">
          {{saveButtonTitle}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { GET_SUBDOMAINS_FOR_TLD, REMOVE_SUBDOMAINS_FOR_TLD } from '@/store/project/actionTypes';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'MoveIpPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    title: String,
    saveButtonTitle: String,
    ipList: {
      type: Array,
      required: true,
    },
    currentSelectedTld: {
      required: false,
    },
    currentSelectedSubdomain: {
      required: false,
    },
    assetsOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      subdomainsOptions: [],
      loadingSubdomains: false,
      selectedIp: this.ipList[0] || '',
      selectedTld: this.currentSelectedTld || '',
      selectedSubdomain: this.currentSelectedSubdomain || '',
    };
  },
  watch: {
    async visiblePopup(value) {
      if (value) {
        this.init();
        await this.$store.dispatch(GET_SUBDOMAINS_FOR_TLD, { tld: this.currentSelectedTld });
      } else {
        this.onClose();
        await this.$store.dispatch(REMOVE_SUBDOMAINS_FOR_TLD);
      }
    },
    subdomainsForTld: {
      handler() {
        this.loadingSubdomains = false;
        this.subdomainsOptions = [...this.subdomainsForTld];
      },
      immediate: true,
      deep: true,
    },
    selectedTld() {
      this.selectedSubdomain = '';
    },
  },
  computed: {
    ...mapGetters(['subdomainsForTld', 'domains', 'configurationDomains']),
    tldKeys() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
  },
  methods: {
    init() {
      this.selectedTld = this.currentSelectedTld;
      // eslint-disable-next-line prefer-destructuring
      this.selectedIp = this.ipList[0];
      this.selectedSubdomain = this.currentSelectedSubdomain;
    },
    onClose() {
      this.loadingSubdomains = false;
    },
    async loadSubdomains(tld) {
      this.loadingSubdomains = true;
      await this.$store.dispatch(GET_SUBDOMAINS_FOR_TLD, { tld });
    },
    onCancelButton() {
      this.$emit('cancel');
    },
    onOkClickButton() {
      const data = { host: this.selectedIp, tld: this.selectedTld, subdomain: this.selectedSubdomain };
      this.$emit('changed', data);
      this.onCancelButton();
    },
  },
};
</script>

<style lang='scss'>
.move-ip-domain-select {
  .v-list-item__title {
    text-transform: lowercase !important;
    font-size: 18px;
  }
  .v-autocomplete__content.v-menu__content {
    min-width: 300px !important;
  }
  .v-menu__content {
    min-width: 300px !important;
  }
  .v-menu__content.v-list-item__title {
    text-transform: lowercase !important;
    font-size: 18px;
  }
}
.move-ip-popup {
  .v-icon.v-icon {
    color: #C0C4CC !important;
    font-size: 22px;
    font-weight: lighter;
  }

  // Override Vuetify's .v-card__title which inflates .title via cascade
  .title {
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    line-height: 1.4 !important;
    text-transform: none !important;
    word-break: normal !important;
  }
}
</style>
<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.select {
  margin-top: rem(15);
}
.popup-wrapper {
  max-width: rem(380);
  padding: $space-8 rem(15);

  .select-host {
    .v-input {
      margin-top: 0 !important;
    }
    &__title {
      font-family: $font-family-regular;
      font-size: $font-size-2xs;
      letter-spacing: 0.8px;
      line-height: $font-size-2xs;
      margin-bottom: $space-1-5;
      color: var(--text-secondary);
    }
  }

  .select-domain {
    .v-input {
      margin-top: 0 !important;
    }
    &__title {
      font-family: $font-family-regular;
      font-size: $font-size-2xs;
      letter-spacing: 0.8px;
      line-height: $font-size-2xs;
      margin-bottom: $space-1-5;
      color: var(--text-secondary);
    }
  }

  .select-subdomain {
    .v-input {
      margin-top: 0 !important;
    }
    &__title {
      font-family: $font-family-regular;
      font-size: $font-size-2xs;
      letter-spacing: 0.8px;
      line-height: $font-size-2xs;
      margin-bottom: $space-1-5;
      color: var(--text-secondary);
    }
  }

  .title {
    color: var(--text-primary);
    font-size: rem(16) !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    line-height: 1.4 !important;
    text-transform: none !important;
    padding-bottom: rem(23);
    word-break: normal !important;
  }

  .message {
    font-size: $font-size-base;
    line-height: 1.36;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;
    line-height: rem(18);
  }

  .buttons {
    text-align: right;
    padding-top: rem(30);

    .no-button {
      box-sizing: border-box;
      height: $btn-height-md;
      width: rem(86);
      border: $border-width-thin solid var(--brand-primary);
      color: var(--brand-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      box-shadow: $shadow-sm;
    }

    .yes-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary) !important;
      box-shadow: $shadow-sm;
      color: var(--text-inverse);
      font-size: $font-size-xs;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }
}
</style>
